import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { Formik } from "formik";

import Alert from "components/Alert";
import Button from "components/Button";

import { flushErrors } from "actions/errorActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { updateEntityLimit } from "actions/adminActions";
import { toast } from "react-toastify";
import { adminConstants } from "actions/types";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import type { RootState } from "types/state";

import * as yup from "yup";

const schema = yup.object({
  companyLimit: yup.number().label("Organization Company Limit").required(),
});

interface FormValues {
  companyLimit: number;
}

interface OwnProps {
  onClose: () => void;
  onSuccess: (companyLimit: number) => void;
  companyLimit: number;
  // companyId / organizationId are passed in by the parent (Organization).
  // The submit path reads organizationId; companyId is declared but unused here.
  companyId?: string;
  organizationId?: string;
}

const orgEvent = adminConstants.UPDATE_ENTITY_LIMIT;

const loadingSelector = createLoadingSelector(orgEvent);
const errorSelector = createErrorSelector(orgEvent);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: loadingSelector(state),
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  flushErrors,
  updateEntityLimit,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = WithApolloClient<OwnProps & ConnectedProps<typeof connector>>;

class ChangeOrgLimitModal extends React.PureComponent<Props> {
  _onClose = () => {
    this.props.flushErrors();
    this.props.onClose();
  };

  _submitNewOrgLimit = async ({ companyLimit }: FormValues) => {
    const result = await this.props.updateEntityLimit(
      this.props.client as ApolloClient<object>,
      {
        entityId: this.props.organizationId,
        entityType: "ORGANIZATION",
        key: "OrganizationCompanyLimit",
        value: companyLimit.toString(),
      }
    );

    if (result.success) {
      toast.success("Successfully updated company limit.");
      this.props.onSuccess(companyLimit);
    }
  };

  render() {
    return (
      <Formik<FormValues>
        validateOnChange={false}
        validationSchema={schema}
        onSubmit={(values: FormValues) => {
          void this._submitNewOrgLimit(values);
        }}
        enableReinitialize={true}
        initialValues={{
          companyLimit: this.props.companyLimit,
        }}
      >
        {({ handleSubmit, handleChange, values, touched, errors }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Modal
              centered
              scrollable
              show={true}
              onHide={this._onClose}
              className="icon-modal"
            >
              <Modal.Header closeButton>
                Organization Company Limits
              </Modal.Header>

              <Modal.Body>
                <Row className="mb-3">
                  <Form.Group as={Col} controlId="formGroupName">
                    <Form.Label>
                      Max number of companies for Organization
                    </Form.Label>
                    <Form.Control
                      name="companyLimit"
                      type={"number"}
                      placeholder="Organization Company Limit"
                      value={values.companyLimit}
                      onChange={handleChange}
                      isInvalid={!!errors.companyLimit}
                      isValid={touched.companyLimit && !errors.companyLimit}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.companyLimit}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
                {this.props.error && (
                  <Alert type="error" msg={this.props.error} />
                )}
              </Modal.Body>
              <Modal.Footer>
                <div className="submit-row">
                  <Button
                    btnLabel="Update Company Limit"
                    name="submit"
                    withArrow={true}
                    loading={this.props.isFetching}
                    // @ts-expect-error Formik handleSubmit expects a FormEvent;
                    // master calls it with `values` instead (inert at runtime).
                    onClick={() => handleSubmit(values)}
                  />
                  <Button
                    btnLabel="Cancel"
                    name="cancel"
                    color="cancel"
                    type="button"
                    onClick={this.props.onClose}
                  />
                </div>
              </Modal.Footer>
            </Modal>
          </Form>
        )}
      </Formik>
    );
  }
}

export default connector(withApollo<Props>(ChangeOrgLimitModal));
